import { useState } from 'react'
import { SectionHeader, Panel, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { Forbidden, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { FilterBar } from '@/shared/ui/filters'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import {
  capturePledge, identifyByNationalId, listPledges, pledgeCaptureChannels, pledgeIntents,
  pledgeSlots, pledgeTransportModes, type PledgeInput, type TurnoutIdentity,
} from './api'
import { label } from './vocab'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'

/**
 * Pledges — what each member plans to do on polling day.
 *
 * ## This page was written against an API that does not exist
 *
 * Every part of it was invented and none of it worked:
 *
 * - It gated on `turnout:read-pledges` / `turnout:capture-pledges`. **Neither authority
 *   exists.** The real ones are `turnout:read` and `turnout:pledge`. It only rendered at
 *   all because `useCan` returns true for anything when the caller holds `*:*`.
 * - It listed with `GET /turnout/pledges?stationId=…`. That endpoint takes
 *   `electionId` + `voterId` and returns ONE pledge; there is no station variant. The call
 *   bound nothing, came back 400, and `.then(r => r.json())` handed the problem document
 *   to `.map` — which is the `map is not a function` crash.
 * - Its codes were guesses: `DEFINITE`, `PROBABLE`, `EARLY_MORNING`, `FOOT`, `NEEDS_LIFT`.
 *   The real vocabularies are `COMMITTED`/`LIKELY`/`UNSURE`, `BEFORE_0700`/`H0700_0900`,
 *   `WALK`/`MATATU`/`BODA`. Every one is SERVED, so none of them belongs in this file.
 * - It posted `stationId`, `timeSlotCode`, `needsLift`, `channelCode` — none of which are
 *   fields on `PledgeRequest`.
 *
 * The list is by election and optionally by intent, which is what the server offers and
 * what the screen is for.
 */
export function PledgesPage() {
  const canRead = useCan('turnout:read')
  const canCapture = useCan('turnout:pledge')

  const selection = useElection()
  const { electionId } = selection
  const [intent, setIntent] = useState('')

  const intents = useLoad(pledgeIntents, [])
  const pledges = useLoad(
    () => (electionId ? listPledges(electionId, intent || undefined) : Promise.resolve([])),
    [electionId, intent])

  if (!canRead) return <Forbidden what="pledges" />

  const rows = pledges.data ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Pledges"
        description="When, from where and how each member plans to vote. A plan predicts turnout; a yes/no does not."
        right={<ElectionPicker selection={selection} />}
      />

      <ErrorStrip message={selection.error ?? pledges.error} />

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Intent
          <select className="erp-input h-9" value={intent}
            disabled={intents.loading}
            onChange={(e) => setIntent(e.target.value)}>
            <option value="">All</option>
            {(intents.data ?? []).map((c) => (
              <option key={c} value={c}>{label(c)}</option>
            ))}
          </select>
        </label>
      </FilterBar>

      {pledges.loading && rows.length === 0 ? (
        <LoadingBlock label="Loading pledges…" />
      ) : (
        <Panel
          title={`${rows.length} pledge${rows.length === 1 ? '' : 's'}`}
          description="One live pledge per member. Changing it supersedes the old one; both are kept, which is what intent decay reads."
        >
          <ErpTable head={(
            <>
              <Th>Voter</Th>
              <Th>Intent</Th>
              <Th>When</Th>
              <Th>How</Th>
              <Th className="text-right">Complete</Th>
              <Th>Transport gap</Th>
              <Th>Captured</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7} message={intent
                ? `No pledge in this election has intent ${intent}.`
                : 'No pledges captured yet. Capture them at enrolment or on the doorstep.'} />
            ) : rows.map((p) => (
              <tr key={p.id} className="hover:bg-muted/40">
                <Td>
                  {/* The server decides what this says: the full name for a caller
                      holding turnout:read-raw, initials otherwise. Whatever comes back
                      is the whole of what belongs here.

                      There used to be a second line under it carrying the first eight
                      characters of the voter uuid, on every row, unconditionally — under
                      a comment claiming this was "not a uuid either way". Nobody can read
                      a name out of `01a05379`, nobody can say it down a phone, and it is
                      an internal identifier on a screen used in front of the people it
                      identifies.

                      A null name means the register does not hold that voter — either it
                      never did, or the row has since gone. Worth stating in words: with
                      the id shown instead, 304 seeded pledges pointing at voters who were
                      never inserted looked exactly like 304 ordinary rows. */}
                  <span className="block">
                    {p.voterDisplayName ?? (
                      <span className="text-muted-foreground italic">
                        Not in the register
                      </span>
                    )}
                  </span>
                </Td>
                <Td>{label(p.intentCode)}</Td>
                <Td className="text-muted-foreground">{label(p.plannedSlotCode)}</Td>
                <Td className="text-muted-foreground">{label(p.transportModeCode)}</Td>
                <Td className="text-right tabular-nums">
                  {/* Null is not zero: a plan with no completeness computed and a plan
                      that is 0% complete are different statements. */}
                  {p.completeness === null ? '—' : `${Math.round(p.completeness * 100)}%`}
                </Td>
                <Td>
                  {p.hasTransportGap
                    ? <span className="text-amber-700 font-medium">needs a lift</span>
                    : <span className="text-muted-foreground">—</span>}
                </Td>
                <Td className="text-muted-foreground">{fmtDateTime(p.capturedAt)}</Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      <CapturePanel electionId={electionId} canCapture={canCapture}
        onCaptured={() => pledges.reload()} />
    </div>
  )
}

/** Capture one pledge. Every code list is served — none is hardcoded here. */
function CapturePanel({
  electionId, canCapture, onCaptured,
}: { electionId: string | null; canCapture: boolean; onCaptured: () => void }) {
  const intents = useLoad(pledgeIntents, [])
  const slots = useLoad(pledgeSlots, [])
  const modes = useLoad(pledgeTransportModes, [])
  const channels = useLoad(pledgeCaptureChannels, [])

  const [nationalId, setNationalId] = useState('')
  const [identity, setIdentity] = useState<TurnoutIdentity | null>(null)
  const [identifying, setIdentifying] = useState(false)
  const [intentCode, setIntentCode] = useState('')
  const [plannedSlotCode, setSlot] = useState('')
  const [transportModeCode, setMode] = useState('')
  const [captureChannelCode, setChannel] = useState('')
  const [consent, setConsent] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Somebody who is not going has no departure time and no transport. Asking anyway is
  // how a form collects answers nobody meant.
  const going = intentCode === 'COMMITTED' || intentCode === 'LIKELY'

  const identify = async () => {
    setIdentifying(true)
    setError(null)
    setIdentity(null)
    try {
      setIdentity(await identifyByNationalId(nationalId.trim()))
    } catch (e) {
      setError(explain(e))
    } finally {
      setIdentifying(false)
    }
  }

  const submit = async () => {
    if (!electionId || !identity?.voterId) return
    setSaving(true)
    setError(null)
    try {
      const body: PledgeInput = {
        electionId,
        voterId: identity.voterId,
        intentCode,
        plannedSlotCode: going ? plannedSlotCode || null : null,
        transportModeCode: going ? transportModeCode || null : null,
        captureChannelCode,
        consentToReminders: consent,
      }
      await capturePledge(body)
      setNationalId('')
      setIdentity(null)
      setConsent(false)
      onCaptured()
    } catch (e) {
      setError(explain(e))
    } finally {
      setSaving(false)
    }
  }

  // A pledge is keyed on a voter row, so an eCitizen-only match is not enough: that person
  // is real but is not a member yet, and the lookup's sentence says so.
  const incomplete = !electionId || !identity?.voterId || !intentCode || !captureChannelCode

  return (
    <Panel title="Capture a pledge"
      description="One live pledge per member. Capturing again supersedes the previous one.">
      <div className="p-4 space-y-3 max-w-2xl">
        <ErrorStrip message={error} />

        {/* Nobody can type a uuid. The coordinator has a person and an ID card. */}
        <div className="flex items-end gap-2">
          <label className="text-erp-xs space-y-1 block flex-1 max-w-xs">
            <span>National ID</span>
            <input className="erp-input h-9 w-full" value={nationalId}
              inputMode="numeric"
              onChange={(e) => { setNationalId(e.target.value); setIdentity(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter' && nationalId.trim()) void identify() }}
              placeholder="e.g. 31415926" />
          </label>
          <button type="button" className="erp-btn h-9"
            disabled={identifying || !nationalId.trim()}
            onClick={() => { void identify() }}>
            {identifying ? 'Looking up…' : 'Find'}
          </button>
        </div>

        {identity && (
          <Note tone={identity.inRegister ? 'info' : 'bad'}>
            {identity.displayName && (
              <div className="font-medium text-erp-md">{identity.displayName}</div>
            )}
            {/* The server composes the sentence, so every client says the same thing and a
                refusal is never rendered as a blank. */}
            <div>{identity.sentence}</div>
          </Note>
        )}

        {identity?.inRegister && (
          <label className="text-erp-xs space-y-1 block max-w-xs">
            <span>Intent</span>
            <select className="erp-input h-9 w-full" value={intentCode}
              onChange={(e) => setIntentCode(e.target.value)}>
              <option value="">Choose…</option>
              {(intents.data ?? []).map((c) => (
                <option key={c} value={c}>{label(c)}</option>
              ))}
            </select>
          </label>
        )}

        {identity?.inRegister && going && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-erp-xs space-y-1 block">
              <span>Time slot</span>
              <select className="erp-input h-9 w-full" value={plannedSlotCode}
                onChange={(e) => setSlot(e.target.value)}>
                <option value="">Undecided</option>
                {(slots.data ?? []).map((c) => (
                  <option key={c} value={c}>{label(c)}</option>
                ))}
              </select>
            </label>
            <label className="text-erp-xs space-y-1 block">
              <span>Transport</span>
              <select className="erp-input h-9 w-full" value={transportModeCode}
                onChange={(e) => setMode(e.target.value)}>
                <option value="">Undecided</option>
                {(modes.data ?? []).map((c) => (
                  <option key={c} value={c}>{label(c)}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        <label className="text-erp-xs space-y-1 block max-w-xs">
          <span>How was this captured?</span>
          <select className="erp-input h-9 w-full" value={captureChannelCode}
            onChange={(e) => setChannel(e.target.value)}>
            <option value="">Choose…</option>
            {(channels.data ?? []).map((c) => (
              <option key={c} value={c}>{label(c)}</option>
            ))}
          </select>
        </label>

        {identity?.inRegister && (
          <label className="flex items-start gap-2 text-erp-xs max-w-xl">
            <input type="checkbox" className="mt-0.5" checked={consent}
              onChange={(e) => setConsent(e.target.checked)} />
            <span>
              They agreed to be contacted with reminders about voting.
              <span className="block text-muted-foreground">
                Optional — a plan can be captured without it. Ticking this writes a consent
                record naming you and how they were asked; leaving it writes nothing. It is
                append-only, so it cannot be quietly corrected later.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center gap-3">
          {/* Offered without turnout:pledge — the server's refusal is what speaks. */}
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={saving || incomplete} onClick={() => { void submit() }}>
            {saving ? 'Capturing…' : 'Capture pledge'}
          </button>
          {!canCapture && (
            <span className="text-erp-xs text-muted-foreground">
              Needs <code>turnout:pledge</code>.
            </span>
          )}
        </div>

        <Note>
          Asking <em>when, from where and how</em> is the point. "Will you vote?" gets a
          near-universal yes and predicts almost nothing.
        </Note>
      </div>
    </Panel>
  )
}
